import { Person } from '@api/lendingAPI/CustomerStateAPIHelper';
import { IconCheckDefault, IconFileClock } from '@iwoca/icons';
import { Pill, Text, Button } from '@iwoca/orion';
import { upperFirst } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { buildAddressLabel } from '../../../../../../forms/autoAddress/AutoAddressHelper';
import { FormattedName } from '../../../../../../iwIntlProvider/FormattedName';

type TShareholderCardProps = {
    person: Person;
    status: 'confirmed' | 'incomplete';
    onEdit?: () => void;
    onRemove?: () => void;
    disabled?: boolean;
};

export const ShareholderCard = ({
    person,
    status,
    onEdit,
    onRemove,
    disabled
}: TShareholderCardProps) => {
    const isConfirmed = status === 'confirmed';
    const address = person.addresses?.[0];
    const dob = person.dateOfBirth;

    return (
        <div className="mb-l rounded-s border border-structure-80 p-l">
            <Pills person={person} />

            <div className="mt-m flex gap-m">
                <div className="hidden min-m:block">
                    {isConfirmed ? (
                        <IconCheckDefault className="shrink-0 text-l text-success-drk" />
                    ) : (
                        <IconFileClock className="shrink-0 text-l text-warning-drk" />
                    )}
                </div>
                <div className="flex flex-col gap-m">
                    <Text className="text-s font-med">
                        {person.title && `${upperFirst(person.title)} `}
                        <FormattedName
                            firstName={person.firstName}
                            lastName={person.lastName}
                        />
                    </Text>

                    {dob?.month && (
                        <Text className="text-s font-med">
                            {dob.day && `${dob.day}/`}
                            {dob.month}/{dob.year}
                        </Text>
                    )}

                    {address && (
                        <Text className="text-s font-med">
                            {buildAddressLabel(address)}
                        </Text>
                    )}
                </div>
            </div>

            {(onEdit || onRemove) && (
                <div className="mt-m border-t border-structure-80 pt-m">
                    <div className="flex items-center justify-between">
                        {onEdit && (
                            <Button
                                type="button"
                                variant="tertiary"
                                className="h-auto p-0 disabled:border-none disabled:bg-transparent"
                                size="small"
                                onClick={onEdit}
                                disabled={disabled}
                            >
                                {isConfirmed ? (
                                    <FormattedMessage
                                        id="_.funding_requirements.shareholders.edit"
                                        defaultMessage="Edit"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="_.funding_requirements.shareholders.complete_details"
                                        defaultMessage="Complete details"
                                    />
                                )}
                            </Button>
                        )}
                        {onRemove && (
                            <Button
                                type="button"
                                variant="tertiary"
                                className="h-auto p-0"
                                size="small"
                                onClick={onRemove}
                                disabled={disabled}
                            >
                                <FormattedMessage
                                    id="_.funding_requirements.shareholders.remove"
                                    defaultMessage="Remove"
                                />
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const Pills = ({ person }: { person: Person }) => {
    const ROLE_PILLS = [
        {
            key: 'isApplicant',
            variant: 'green',
            label: (
                <FormattedMessage
                    id="_.funding_requirements.shareholders.pill.this_is_you"
                    defaultMessage="This is you"
                />
            )
        },
        {
            key: 'isGuarantor',
            variant: 'london',
            label: (
                <FormattedMessage
                    id="_.funding_requirements.shareholders.pill.guarantor"
                    defaultMessage="Guarantor"
                />
            )
        },
        {
            key: 'isDirector',
            variant: 'yellow',
            label: (
                <FormattedMessage
                    id="_.funding_requirements.shareholders.pill.director"
                    defaultMessage="Director"
                />
            )
        }
    ] as const;

    return (
        <div className="flex flex-wrap gap-s">
            {ROLE_PILLS.filter(({ key }) => person[key]).map(
                ({ key, variant, label }) => (
                    <Pill key={key} variant={variant} size="small">
                        {label}
                    </Pill>
                )
            )}
            {person.isShareholder ? (
                <Pill variant="red" size="small">
                    <FormattedMessage
                        id="_.funding_requirements.shareholders.pill.major_shareholder"
                        defaultMessage="Major Shareholder"
                    />
                </Pill>
            ) : (
                <span className="rounded-full border border-structure-80 px-s py-2xs text-s text-structure-40">
                    <FormattedMessage
                        id="_.funding_requirements.shareholders.pill.not_major_shareholder"
                        defaultMessage="Not Major Shareholder"
                    />
                </span>
            )}
        </div>
    );
};
